import React from 'react';
import { Group, Select, Text, ActionIcon, TextInput } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { Relationship, RelationshipType, RelationshipTypes, formatRelationshipTypeForDisplay } from '../../../types/Person';

// Helper function to find the matching relationship type in the enum
const findMatchingRelationshipType = (type: string | null): RelationshipType | undefined => {
  if (!type) return undefined;
  
  // First try direct match
  if (Object.values(RelationshipType).includes(type as any)) {
    return type as RelationshipType;
  }
  
  // Try to match by display value
  const displayValue = formatRelationshipTypeForDisplay(type);
  for (const enumType of RelationshipTypes) {
    if (formatRelationshipTypeForDisplay(enumType) === displayValue) {
      return enumType;
    }
  }
  
  // Try to match by standardized value
  const standardized = type.toLowerCase();
  for (const enumType of RelationshipTypes) {
    if (enumType.toLowerCase() === standardized) {
      return enumType;
    }
  }
  
  console.log(`Could not find matching relationship type for: ${type}`);
  return undefined;
};

interface RelationshipListProps {
  relationships: Relationship[];
  onUpdateRelationship: (index: number, field: keyof Relationship, value: any) => void;
  onRemoveRelationship: (index: number) => void;
  peopleOptions: { value: string; label: string }[];
  disabled?: boolean;
}

export const RelationshipList: React.FC<RelationshipListProps> = ({
  relationships,
  onUpdateRelationship,
  onRemoveRelationship,
  peopleOptions,
  disabled = false,
}) => {
  return (
    <>
      {relationships.map((relationship, index) => (
        <Group key={index} align="flex-start" justify="space-between">
          <Select
            data={RelationshipTypes.map(type => ({
              value: type,
              label: formatRelationshipTypeForDisplay(type)
            }))}
            value={findMatchingRelationshipType(relationship.type as string) || undefined}
            onChange={(value) => onUpdateRelationship(index, 'type', value)}
            placeholder="Select relationship type"
            style={{ flex: 1 }}
            searchable
            clearable
            disabled={disabled}
          />
          <Text>of</Text>
          {relationship.type === RelationshipType.IsOtherOf && (
            <TextInput
              value={relationship.custom_type || ''}
              onChange={(e) => onUpdateRelationship(index, 'custom_type', e.currentTarget.value)}
              placeholder="Custom relationship type"
              style={{ flex: 1 }}
              disabled={disabled}
            />
          )}
          <Select
            data={peopleOptions}
            value={relationship.name || ''}
            onChange={(value) => onUpdateRelationship(index, 'name', value)}
            placeholder="Select Person"
            style={{ flex: 1 }}
            disabled={disabled}
          />
          <ActionIcon
            color="red"
            onClick={() => onRemoveRelationship(index)}
            variant="outline"
            disabled={disabled}
          >
            <IconTrash size={16} />
          </ActionIcon>
        </Group>
      ))}
    </>
  );
};
